import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Flag, 
  Users, 
  Mail, 
  Bell, 
  Image, 
  LogOut,
  Menu,
  X,
  BarChart3,
  Quote,
  HelpCircle,
  CreditCard,
  UserCircle,
  Car
} from 'lucide-react';

const AdminLayout = () => {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin');
    }
  }, [user, loading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#A19D98]">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/members', icon: UserCircle, label: 'Members' },
    { path: '/admin/vehicles', icon: Car, label: 'Vehicles' },
    { path: '/admin/payments', icon: CreditCard, label: 'Payments' },
    { path: '/admin/rallies', icon: Flag, label: 'Rallies' },
    { path: '/admin/sponsors', icon: Users, label: 'Sponsors' },
    { path: '/admin/signups', icon: Mail, label: 'Rally Signups' },
    { path: '/admin/inquiries', icon: Mail, label: 'Sponsor Inquiries' },
    { path: '/admin/notify', icon: Bell, label: 'Notify List' },
    { path: '/admin/gallery', icon: Image, label: 'Gallery' },
    { path: '/admin/testimonials', icon: Quote, label: 'Testimonials' },
    { path: '/admin/faqs', icon: HelpCircle, label: 'FAQs' },
  ];

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className={`admin-sidebar fixed md:static inset-y-0 left-0 z-50 w-64 transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0 transition-transform duration-200`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-[#2A2A2A]">
            <Link to="/" className="font-['Oswald'] text-xl text-[#F2EDE6]">
              REVLINE RALLY
            </Link>
            <p className="text-xs text-[#A19D98] mt-1">Admin Panel</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`admin-nav-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-6 border-t border-[#2A2A2A]">
            <p className="text-sm text-[#A19D98] mb-4">{user.email}</p>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="admin-nav-item w-full text-left text-[#FF3B30] hover:text-[#FF3B30]"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Mobile header */}
        <header className="md:hidden flex items-center justify-between p-4 border-b border-[#2A2A2A]">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-[#F2EDE6] p-2"
          >
            <Menu size={24} />
          </button>
          <span className="font-['Oswald'] text-[#F2EDE6]">ADMIN</span>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
